import React, { useEffect, useRef, useState, useMemo } from 'react';
import {
    ModalBody,
    Header,
    HeaderSubtitle,
    HeaderTitle,
    ModalContainer,
    Overlay,
    ContentWrapper,
    ComparisonTable,
    ComparisonRow,
    ComparisonCell,
    Description,
    Optional,
} from './package-benefits-modal.styles';
import MaterialIcon from '../../components/material-icon';
import { colorPalette } from '../../utils/theme';
import useIsMobile from '../../hooks/useIsMobile';
import { useTranslation } from 'react-i18next';
import { TFunction } from 'i18next';

interface SubItem {
    description: string;
    optionalDescription?: string;
    includedInBasic: boolean;
    includedInPremium: boolean;
}

interface BenefitItem {
    title: string;
    subItems: SubItem[];
}

// Function to generate benefits data based on translations
const getBenefits = (t: TFunction): BenefitItem[] => [
    {
        title: t('package_benefits_modal.categories.marketing_rental'),
        subItems: [
            {
                description: t('package_benefits_modal.services.assigned_contact_person'),
                includedInBasic: true,
                includedInPremium: true,
            },
            {
                description: t('package_benefits_modal.services.rent_price_evaluation'),
                includedInBasic: true,
                includedInPremium: true,
            },
            {
                description: t('package_benefits_modal.services.property_marketing'),
                includedInBasic: true,
                includedInPremium: true,
            },
            {
                description: t('package_benefits_modal.services.tenant_screening'),
                includedInBasic: true,
                includedInPremium: true,
            },
            {
                description: t('package_benefits_modal.services.annual_rent_evaluation'),
                includedInBasic: true,
                includedInPremium: true,
            },
            {
                description: t('package_benefits_modal.services.vacant_property_management'),
                includedInBasic: true,
                includedInPremium: true,
            },
            {
                description: t('package_benefits_modal.services.annual_property_inspection'),
                includedInBasic: false,
                includedInPremium: true,
            },
            {
                description: t('package_benefits_modal.services.landlord_portal_access'),
                optionalDescription: t('package_benefits_modal.services.landlord_portal_details'),
                includedInBasic: true,
                includedInPremium: true,
            },
            {
                description: t('package_benefits_modal.services.annual_property_inspection_duplicate'),
                includedInBasic: false,
                includedInPremium: true,
            },
            {
                description: t('package_benefits_modal.services.rental_contract_preparation'),
                includedInBasic: true,
                includedInPremium: true,
            },
        ],
    },
    {
        title: t('package_benefits_modal.categories.tenant_management'),
        subItems: [
            {
                description: t('package_benefits_modal.services.ejari_registration'),
                includedInBasic: true,
                includedInPremium: true,
            },
            {
                description: t('package_benefits_modal.services.move_in_coordination'),
                includedInBasic: true,
                includedInPremium: true,
            },
            {
                description: t('package_benefits_modal.services.move_out_coordination'),
                includedInBasic: true,
                includedInPremium: true,
            },
            {
                description: t('package_benefits_modal.services.move_inspection_report'),
                includedInBasic: true,
                includedInPremium: true,
            },
            {
                description: t('package_benefits_modal.services.tenant_support_person'),
                includedInBasic: true,
                includedInPremium: true,
            },
            {
                description: t('package_benefits_modal.services.third_party_communication'),
                includedInBasic: true,
                includedInPremium: true,
            },
            {
                description: t('package_benefits_modal.services.lease_renewal_management'),
                includedInBasic: true,
                includedInPremium: true,
            },
        ]
    },
    {
        title: t('package_benefits_modal.categories.rental_income_management'),
        subItems: [
            {
                description: t('package_benefits_modal.services.rent_deposit_management'),
                includedInBasic: true,
                includedInPremium: true,
            },
            {
                description: t('package_benefits_modal.services.rent_check_submission'),
                includedInBasic: true,
                includedInPremium: true,
            },
            {
                description: t('package_benefits_modal.services.rent_accounting_transfers'),
                includedInBasic: true,
                includedInPremium: true,
            },
            {
                description: t('package_benefits_modal.services.deposit_management'),
                includedInBasic: true,
                includedInPremium: true,
            },
            {
                description: t('package_benefits_modal.services.bounced_check_management'),
                includedInBasic: true,
                includedInPremium: true,
            },
            {
                description: t('package_benefits_modal.services.service_charge_coordination'),
                includedInBasic: false,
                includedInPremium: true,
            },
            {
                description: t('package_benefits_modal.services.additional_payments_organization'),
                includedInBasic: false,
                includedInPremium: true,
            },
            {
                description: t('package_benefits_modal.services.debt_collection_management'),
                includedInBasic: false,
                includedInPremium: true,
            },
        ]
    },
    {
        title: t('package_benefits_modal.categories.inspection_maintenance'),
        subItems: [
            {
                description: t('package_benefits_modal.services.sale_viewing_arrangements'),
                includedInBasic: true,
                includedInPremium: true,
            },
            {
                description: t('package_benefits_modal.services.maintenance_request_processing'),
                includedInBasic: true,
                includedInPremium: true,
            },
            {
                description: t('package_benefits_modal.services.maintenance_coordination'),
                includedInBasic: true,
                includedInPremium: true,
            },
            {
                description: t('package_benefits_modal.services.maintenance_quality_check'),
                includedInBasic: true,
                includedInPremium: true,
            },
            {
                description: t('package_benefits_modal.services.warranty_management'),
                includedInBasic: false,
                includedInPremium: true,
            }
        ]
    },
    {
        title: t('package_benefits_modal.categories.handover_package'),
        subItems: [
            {
                description: t('package_benefits_modal.services.handover_snagging_inspection'),
                includedInBasic: true,
                includedInPremium: true,
            },
            {
                description: t('package_benefits_modal.services.de_snagging_coordination'),
                includedInBasic: true,
                includedInPremium: true,
            },
            {
                description: t('package_benefits_modal.services.utility_registration'),
                includedInBasic: true,
                includedInPremium: true,
            },
            {
                description: t('package_benefits_modal.services.final_payment_coordination'),
                includedInBasic: true,
                includedInPremium: true,
            },
            {
                description: t('package_benefits_modal.services.key_handover'),
                includedInBasic: true,
                includedInPremium: true,
            },
        ]
    }
];

interface PackageBenefitsModalProps {
    isVisible: boolean;
    onClose: () => void;
}

const PackageBenefitsModal: React.FC<PackageBenefitsModalProps> = ({ isVisible, onClose }) => {
    const isMobile = useIsMobile();
    const { t } = useTranslation();

    // Memoized benefits data that updates when language changes
    const benefits = useMemo(() => getBenefits(t), [t]);

    // States
    const [isClosing, setIsClosing] = useState(false);
    const closeTimeoutRef = useRef<NodeJS.Timeout | null>(null);

    // Functions
    const handleClose = () => {
        setIsClosing(true);
        closeTimeoutRef.current = setTimeout(() => {
            onClose();
            setIsClosing(false);
        }, 300);
    };

    useEffect(() => {
        return () => {
            if (closeTimeoutRef.current) clearTimeout(closeTimeoutRef.current);
        };
    }, []);

    // Prevent scrolling down
    useEffect(() => {
        if (isVisible) {
            // Disable background scrolling
            document.body.style.overflow = 'hidden';

            // Prevent touchmove only if the event is outside the modal
            const preventBackgroundScroll = (e: TouchEvent) => {
                if (!(e.target as HTMLElement).closest('.sidebar-modal-content')) {
                    e.preventDefault();
                }
            };

            document.addEventListener('touchmove', preventBackgroundScroll, { passive: false });

            return () => {
                // Restore background scrolling
                document.body.style.overflow = 'unset';
                document.removeEventListener('touchmove', preventBackgroundScroll);
            };
        }
    }, [isVisible]);

    if (!isVisible && !isClosing) return null;

    return (
        <Overlay onClick={handleClose}>
            <ModalContainer onClick={(e) => e.stopPropagation()} isClosing={isClosing}>
                <Header>
                    <div>
                        <HeaderTitle>{t('package_benefits_modal.header.title')}</HeaderTitle>
                        <HeaderSubtitle>{t('package_benefits_modal.header.subtitle')}</HeaderSubtitle>
                    </div>
                    <MaterialIcon icon="close" size={24} color={colorPalette.blue500} onClick={handleClose} />
                </Header>
                <ModalBody className="sidebar-modal-content">
                    <ContentWrapper>
                        <ComparisonTable>
                            <thead>
                                <tr>
                                    <th>{t('package_benefits_modal.table_headers.service')}</th>
                                    <th>{t('package_benefits_modal.table_headers.basic')}</th>
                                    <th>{t('package_benefits_modal.table_headers.premium')}</th>
                                </tr>
                            </thead>
                            <tbody>
                                {benefits.map((benefit, benefitIndex) => (
                                    <React.Fragment key={benefitIndex}>
                                        {/* Render the main headline */}
                                        <ComparisonRow>
                                            <ComparisonCell colSpan={3}>
                                                <strong>{benefit.title}</strong>
                                            </ComparisonCell>
                                        </ComparisonRow>
                                        {/* Render sub-items */}
                                        {benefit.subItems.map((subItem, subItemIndex) => (
                                            <ComparisonRow key={subItemIndex}>
                                                <ComparisonCell style={{ paddingLeft: isMobile ? 8 : 16 }}>
                                                    <Description>{subItem.description}</Description>
                                                    {subItem.optionalDescription && (
                                                        <Optional dangerouslySetInnerHTML={{ __html: subItem.optionalDescription }} />
                                                    )}
                                                </ComparisonCell>
                                                <ComparisonCell className="text-center">
                                                    {subItem.includedInBasic ? (
                                                        <MaterialIcon icon="check" color={colorPalette.successMedium} size={24} />
                                                    ) : (
                                                        <MaterialIcon icon="close" color={colorPalette.blue300} size={24} />
                                                    )}
                                                </ComparisonCell>
                                                <ComparisonCell className="text-center">
                                                    {subItem.includedInPremium ? (
                                                        <MaterialIcon icon="check" color={colorPalette.successMedium} size={24} />
                                                    ) : (
                                                        <MaterialIcon icon="close" color={colorPalette.blue300} size={24} />
                                                    )}
                                                </ComparisonCell>
                                            </ComparisonRow>
                                        ))}
                                    </React.Fragment>
                                ))}
                            </tbody>
                        </ComparisonTable>
                    </ContentWrapper>
                </ModalBody>
            </ModalContainer>
        </Overlay>
    );
};

export default PackageBenefitsModal;
